<template>
    <div class="search-product-page glass-theme fade-in-page">

        <!-- ============================================ -->
        <!-- IMAGE VIEWER MODAL                           -->
        <!-- ============================================ -->
        <b-modal
            id="modal-image-viewer"
            size="lg"
            hide-footer
            centered
            :title="currentProductName || 'Product Image'"
            @hidden="onImageViewerHidden"
        >
            <div class="text-center">
                <img
                    v-if="currentFullSizeImage"
                    :src="currentFullSizeImage"
                    :alt="currentProductName"
                    class="full-size-image"
                    @error="onFullImageError"
                />
                <div v-else class="image-fallback-large">
                    <b-icon icon="image" font-scale="5" class="text-muted"></b-icon>
                    <p class="mt-3 text-muted">No image available</p>
                </div>
                <div class="mt-3">
                    <strong>{{ currentProductSku }}</strong>
                </div>
            </div>
            <template #modal-footer>
                <b-button size="md" variant="outline-secondary" @click="$bvModal.hide('modal-image-viewer')">
                    Close
                </b-button>
            </template>
        </b-modal>

        <!-- ============================================ -->
        <!-- LOCATION INVENTORY BREAKDOWN MODAL          -->
        <!-- ============================================ -->
        <b-modal
            id="modal-location-breakdown"
            scrollable
            size="xl"
            hide-footer
            :title="breakdownModalTitle"
            @hidden="onBreakdownModalHidden"
        >
            <div v-if="loadingBreakdown" class="text-center py-5">
                <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
                <p class="mt-3" style="font-size: 1.1rem;">Loading location inventory...</p>
            </div>

            <div v-else>
                <!-- Product summary -->
                <div v-if="hasSelectedProduct" class="product-summary-card mb-4">
                    <b-row>
                        <b-col md="3">
                            <div class="summary-label">SKU</div>
                            <div class="summary-value">{{ selectedProductSku }}</div>
                        </b-col>
                        <b-col md="4">
                            <div class="summary-label">Product Name</div>
                            <div class="summary-value">{{ selectedProductName }}</div>
                        </b-col>
                        <b-col md="2">
                            <div class="summary-label">Total In Stock</div>
                            <div class="summary-value">{{ totalInStock }}</div>
                        </b-col>
                        <b-col md="3">
                            <div class="summary-label">Total Available</div>
                            <div class="summary-value">{{ totalAvailable }}</div>
                        </b-col>
                    </b-row>
                </div>

                <!-- Location breakdown table -->
                <div v-if="locationBreakdown.length > 0" style="overflow-x: auto;">
                    <table class="location-table">
                        <thead>
                            <tr>
                                <th>LOCATION</th>
                                <th>IN STOCK</th>
                                <th>RESERVED</th>
                                <th>AVAILABLE</th>
                                <th>LAYAWAYED</th>
                                <th>MISSING</th>
                                <th>LAST UPDATED</th>
                                <th v-if="isAdmin">ADJUST QTY</th>
                                <th v-if="isAdmin">ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(loc, idx) in locationBreakdown" :key="(loc.locsub || idx) + '-' + idx">
                                <td class="loc-name">{{ getLocationName(loc) }}</td>
                                <td>
                                    <b-input
                                        v-if="loc.editing"
                                        v-model.number="loc.edit_nitems"
                                        type="number"
                                        min="0"
                                        size="md"
                                        class="edit-input"
                                    ></b-input>
                                    <span v-else>{{ loc.nitems || 0 }}</span>
                                </td>
                                <td>{{ loc.reserved || 0 }}</td>
                                <td>{{ loc.available || 0 }}</td>
                                <td>{{ loc.layawayed || 0 }}</td>
                                <td>{{ loc.missing || 0 }}</td>
                                <td>
                                    <small class="text-muted">
                                        {{ loc.updated_at ? moment(loc.updated_at).format('MMM DD, YYYY HH:mm') : 'N/A' }}
                                    </small>
                                </td>
                                <td v-if="isAdmin">
                                    <b-button
                                        v-if="!loc.editing"
                                        size="md"
                                        variant="outline-secondary"
                                        @click="startEditLocation(loc)"
                                    >
                                        Edit
                                    </b-button>
                                    <div v-else class="d-flex justify-content-center">
                                        <b-button
                                            size="md"
                                            variant="primary"
                                            class="mr-1"
                                            :disabled="savingLocation"
                                            @click="saveLocationInventory(loc)"
                                        >
                                            Save
                                        </b-button>
                                        <b-button
                                            size="md"
                                            variant="outline-secondary"
                                            @click="cancelEditLocation(loc)"
                                        >
                                            Cancel
                                        </b-button>
                                    </div>
                                </td>
                                <td v-if="isAdmin">
                                    <b-button
                                        size="md"
                                        variant="outline-secondary"
                                        @click="viewLocationLogs(loc)"
                                    >
                                        Logs
                                    </b-button>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr class="totals-row">
                                <td><strong>TOTAL</strong></td>
                                <td><strong>{{ totalInStock }}</strong></td>
                                <td><strong>{{ totalReserved }}</strong></td>
                                <td><strong>{{ totalAvailable }}</strong></td>
                                <td><strong>{{ totalLayawayed }}</strong></td>
                                <td><strong>{{ totalMissing }}</strong></td>
                                <td colspan="3"></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <b-alert v-else variant="light" show class="mt-3">
                    No location inventory data found for this product.
                </b-alert>
            </div>

            <template #modal-footer>
                <b-button variant="outline-secondary" @click="$bvModal.hide('modal-location-breakdown')">
                    Close
                </b-button>
            </template>
        </b-modal>

        <!-- ============================================ -->
        <!-- HEADER                                       -->
        <!-- ============================================ -->
        <div class="page-header">
            <h1 class="page-title">Search Product</h1>
            <p class="page-subtitle">
                Find inventory by SKU, name, category, vendor, or location
            </p>
        </div>

        <!-- ============================================ -->
        <!-- STATS CARDS                                  -->
        <!-- ============================================ -->
        <div class="stats-grid" v-if="items.length > 0 || hasSearched">
            <div class="stat-card stat-card-primary">
                <div class="stat-card-inner">
                    <div class="stat-icon">
                        <b-icon icon="box-seam" font-scale="2"></b-icon>
                    </div>
                    <div class="stat-info">
                        <div class="stat-label">Products Found</div>
                        <div class="stat-value">{{ items.length }}</div>
                    </div>
                </div>
            </div>

            <div class="stat-card stat-card-info">
                <div class="stat-card-inner">
                    <div class="stat-icon">
                        <b-icon icon="layers" font-scale="2"></b-icon>
                    </div>
                    <div class="stat-info">
                        <div class="stat-label">Total Quantity</div>
                        <div class="stat-value">{{ formatNumber(totals.nitems) }}</div>
                    </div>
                </div>
            </div>

            <div class="stat-card stat-card-warning">
                <div class="stat-card-inner">
                    <div class="stat-icon">
                        <b-icon icon="bookmark-check" font-scale="2"></b-icon>
                    </div>
                    <div class="stat-info">
                        <div class="stat-label">Total Reserved</div>
                        <div class="stat-value">{{ formatNumber(totals.reserved) }}</div>
                    </div>
                </div>
            </div>

            <div class="stat-card stat-card-success">
                <div class="stat-card-inner">
                    <div class="stat-icon">
                        <b-icon icon="check-circle" font-scale="2"></b-icon>
                    </div>
                    <div class="stat-info">
                        <div class="stat-label">Total Available</div>
                        <div class="stat-value">{{ formatNumber(totals.available) }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ============================================ -->
        <!-- FILTERS                                      -->
        <!-- ============================================ -->
        <div class="filters-wrapper glass-card">
            <div class="filters-header">
                <span>Filters</span>
                <span v-if="activeFilterCount > 0" class="filter-count">
                    {{ activeFilterCount }} active
                </span>
            </div>

            <b-row class="g-3">
                <!-- SKU Search -->
                <b-col md="4" class="mb-2">
                    <label class="filter-label">SEARCH SKU / NAME</label>
                    <b-input-group size="lg">
                        <b-form-input
                            v-model="searchQuery"
                            placeholder="Type SKU or product name..."
                            @keyup.enter="runSearch"
                        ></b-form-input>
                        <b-input-group-append>
                            <b-button variant="outline-secondary" @click="runSearch">
                                <b-icon icon="search"></b-icon>
                                Search
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-col>

                <!-- Category -->
                <b-col md="2" class="mb-2">
                    <label class="filter-label">CATEGORY</label>
                    <b-form-select
                        :options="categoryOptions"
                        v-model="category_id"
                        size="lg"
                        @change="onCategoryChange"
                    ></b-form-select>
                </b-col>

                <!-- Sub Category -->
                <b-col md="2" class="mb-2">
                    <label class="filter-label">SUB CATEGORY</label>
                    <b-form-select
                        :options="subcategoryOptions"
                        v-model="subcategory_id"
                        size="lg"
                        :disabled="!category_id"
                    ></b-form-select>
                </b-col>

                <!-- Vendor -->
                <b-col md="2" class="mb-2">
                    <label class="filter-label">VENDOR</label>
                    <b-form-select
                        :options="vendorOptions"
                        v-model="vendor_id"
                        size="lg"
                    ></b-form-select>
                </b-col>

                <!-- Location (admin only) -->
                <b-col v-if="isAdmin" md="2" class="mb-2">
                    <label class="filter-label">LOCATION</label>
                    <b-form-select
                        :options="locationOptions"
                        v-model="location_id"
                        size="lg"
                    ></b-form-select>
                </b-col>
            </b-row>

            <!-- Toggles + Buttons -->
            <div class="filters-bottom">
                <div class="checkboxes-group">
                    <b-form-checkbox v-model="onlyNewArrival" class="mr-4" style="font-size: 16px;">
                        New Arrival Only
                    </b-form-checkbox>
                    <b-form-checkbox v-model="onlyRecentlyReceived" style="font-size: 16px;">
                        Recently Received Only
                    </b-form-checkbox>
                </div>

                <div class="buttons-group">
                    <b-button size="lg" variant="outline-secondary" @click="resetFilters">
                        Reset
                    </b-button>
                    <b-button
                        size="lg"
                        variant="primary"
                        class="ml-2"
                        :disabled="loading"
                        @click="runSearch"
                    >
                        <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                        Search
                    </b-button>
                </div>
            </div>

            <!-- Active filter badges -->
            <div v-if="activeFilterCount > 0" class="active-filters">
                <span class="active-filters-label">Active:</span>
                <span
                    v-for="f in activeFilters"
                    :key="f.key"
                    class="active-filter-tag"
                >
                    {{ f.label }}: {{ f.value }}
                    <b-icon
                        icon="x"
                        class="ml-1"
                        style="cursor:pointer;"
                        @click="removeFilter(f.key)"
                    ></b-icon>
                </span>
                <b-button variant="link" size="md" class="p-0 ml-2" @click="resetFilters">
                    Clear all
                </b-button>
            </div>
        </div>

        <!-- ============================================ -->
        <!-- RESULTS                                      -->
        <!-- ============================================ -->
        <b-overlay
            :show="loading"
            spinner-variant="primary"
            spinner-small
            rounded="sm"
            style="margin-top: 16px;"
        >
            <div v-if="items.length > 0" class="results-container glass-card">
                <div class="results-header">
                    <div class="results-count">
                        Showing <strong>{{ items.length }}</strong> result(s)
                        <span v-if="searchQuery"> for "{{ searchQuery }}"</span>
                    </div>
                </div>

                <div style="overflow-x: auto;">
                    <table class="results-table">
                        <thead>
                            <tr>
                                <th style="min-width: 120px; width: 120px;">IMAGE</th>
                                <th @click="sortby('product_sku')" class="sortable" style="min-width: 160px;">
                                    SKU
                                    <b-icon :icon="sortIcon('product_sku')" size="xs"></b-icon>
                                </th>
                                <th @click="sortby('product_name')" class="sortable" style="min-width: 260px;">
                                    NAME
                                    <b-icon :icon="sortIcon('product_name')" size="xs"></b-icon>
                                </th>
                                <th style="min-width: 200px;">VENDOR</th>
                                <th style="min-width: 180px;">CATEGORY</th>
                                <th style="min-width: 200px;">SUB CATEGORY</th>
                                <th @click="sortby('nitems')" class="sortable" style="min-width: 110px;">
                                    QTY
                                    <b-icon :icon="sortIcon('nitems')" size="xs"></b-icon>
                                </th>
                                <th @click="sortby('reserved')" class="sortable" style="min-width: 120px;">
                                    RESERVED
                                    <b-icon :icon="sortIcon('reserved')" size="xs"></b-icon>
                                </th>
                                <th @click="sortby('available')" class="sortable" style="min-width: 130px;">
                                    AVAILABLE
                                    <b-icon :icon="sortIcon('available')" size="xs"></b-icon>
                                </th>
                                <th style="min-width: 110px;">LOCATIONS</th>
                                <th style="min-width: 140px;">ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, idx) in items" :key="itemKey(item, idx)">
                                <!-- IMAGE CELL — BIGGER -->
                                <td>
                                    <div class="image-cell">
                                        <img
                                            v-if="getProductImage(item)"
                                            :src="getProductImage(item)"
                                            :alt="item.product_name"
                                            class="product-thumb"
                                            @error="handleThumbError($event, item)"
                                            @click="showFullImage(item)"
                                        />
                                        <div
                                            v-else
                                            class="no-image-thumb"
                                            @click="showFullImage(item)"
                                        >
                                            <b-icon icon="image" font-scale="1.6" class="text-muted"></b-icon>
                                        </div>
                                    </div>
                                </td>

                                <td class="text-left">
                                    <strong class="sku-cell">{{ getItemSku(item) }}</strong>
                                    <div class="tags-row">
                                        <span v-if="item.is_new_arrival" class="mini-tag">New</span>
                                        <span v-if="item.recently_received" class="mini-tag">Received</span>
                                    </div>
                                </td>
                                <td class="text-left name-cell">{{ item.product_name || '' }}</td>
                                <td class="text-left vendor-cell">{{ getVendorName(item.vendor_id) }}</td>
                                <td class="text-left category-cell">{{ getCategoryName(item.category_id) }}</td>
                                <td class="text-left subcategory-cell">{{ getSubcategoryName(item.subcategory_id) }}</td>
                                <td><span class="qty-pill qty-primary">{{ item.nitems || item.in_stock || 0 }}</span></td>
                                <td><span class="qty-pill qty-warning">{{ item.reserved || 0 }}</span></td>
                                <td><span class="qty-pill qty-success">{{ item.available || 0 }}</span></td>
                                <td>
                                    <span class="locations-badge">
                                        <b-icon icon="geo-alt" class="mr-1"></b-icon>
                                        {{ getItemLocationCount(item) }}
                                    </span>
                                </td>
                                <td>
                                    <b-button
                                        size="md"
                                        variant="outline-primary"
                                        @click="openLocationBreakdown(item)"
                                    >
                                        <b-icon icon="geo-alt" class="mr-1"></b-icon>
                                        View
                                    </b-button>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr class="totals-row">
                                <td colspan="6" class="text-right"><strong>TOTALS</strong></td>
                                <td><strong>{{ formatNumber(totals.nitems) }}</strong></td>
                                <td><strong>{{ formatNumber(totals.reserved) }}</strong></td>
                                <td><strong>{{ formatNumber(totals.available) }}</strong></td>
                                <td colspan="2"></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div v-else-if="!loading && hasSearched" class="empty-state glass-card">
                <b-icon icon="search" font-scale="3" class="text-muted"></b-icon>
                <h5 class="mt-3">No products found</h5>
                <p class="text-muted">Try adjusting your filters or search term.</p>
            </div>

            <div v-else-if="!loading && !hasSearched" class="empty-state glass-card">
                <b-icon icon="search" font-scale="3" class="text-muted"></b-icon>
                <h5 class="mt-3">Ready to search</h5>
                <p class="text-muted">
                    Enter a SKU or product name, or use the filters above.
                </p>
            </div>
        </b-overlay>
    </div>
</template>

<script>
import moment from "moment";
import Requestor from "@/utilities/Requestor";

export default {
    name: "SearchProduct",
    data() {
        return {
            packet: {},
            moment: moment,

            searchQuery: "",
            category_id: null,
            subcategory_id: null,
            vendor_id: null,
            location_id: null,
            onlyNewArrival: false,
            onlyRecentlyReceived: false,

            categoryOptions: [{ value: null, text: "ALL" }],
            vendorOptions: [{ value: null, text: "ALL" }],
            locationOptions: [{ value: null, text: "ALL" }],
            subcategories: [],

            items: [],
            loading: false,
            hasSearched: false,
            sort_asc: true,
            sort_key: null,

            selectedProduct: {},
            locationBreakdown: [],
            loadingBreakdown: false,
            savingLocation: false,

            currentFullSizeImage: null,
            currentProductName: null,
            currentProductSku: null,

            isAdmin: false,
            user_location_id: null,
        };
    },
    computed: {
        isMobile() {
            return window.innerWidth < 768;
        },
        hasSelectedProduct() {
            const p = this.selectedProduct || {};
            return !!(p.product_sku || p.sku || p.product_name);
        },
        selectedProductSku() {
            const p = this.selectedProduct || {};
            return p.product_sku || p.sku || "";
        },
        selectedProductName() {
            const p = this.selectedProduct || {};
            return p.product_name || "";
        },
        breakdownModalTitle() {
            if (!this.hasSelectedProduct) return "Location Breakdown";
            return `Location Breakdown — ${this.selectedProductName} (${this.selectedProductSku})`;
        },
        subcategoryOptions() {
            if (!this.category_id) {
                return [{ value: null, text: "Select category first" }];
            }
            const filtered = this.subcategories.filter(
                (s) => String(s.category_id) === String(this.category_id)
            );
            return [
                { value: null, text: "ALL" },
                ...filtered.map((s) => ({
                    value: s.id,
                    text: s.subcategory || s.name,
                })),
            ];
        },
        activeFilterCount() {
            let c = 0;
            if (this.searchQuery) c++;
            if (this.category_id) c++;
            if (this.subcategory_id) c++;
            if (this.vendor_id) c++;
            if (this.location_id && this.isAdmin) c++;
            if (this.onlyNewArrival) c++;
            if (this.onlyRecentlyReceived) c++;
            return c;
        },
        activeFilters() {
            const f = [];
            if (this.searchQuery)
                f.push({ key: "search", label: "Search", value: this.searchQuery });
            if (this.category_id)
                f.push({
                    key: "category",
                    label: "Category",
                    value: this.getCategoryName(this.category_id),
                });
            if (this.subcategory_id)
                f.push({
                    key: "subcategory",
                    label: "Sub Cat",
                    value: this.getSubcategoryName(this.subcategory_id),
                });
            if (this.vendor_id)
                f.push({
                    key: "vendor",
                    label: "Vendor",
                    value: this.getVendorName(this.vendor_id),
                });
            if (this.location_id && this.isAdmin)
                f.push({
                    key: "location",
                    label: "Location",
                    value: this.getLocationName({ locsub: this.location_id }),
                });
            if (this.onlyNewArrival)
                f.push({ key: "newArrival", label: "Filter", value: "New Arrivals" });
            if (this.onlyRecentlyReceived)
                f.push({
                    key: "recentlyReceived",
                    label: "Filter",
                    value: "Recently Received",
                });
            return f;
        },
        totals() {
            return this.items.reduce(
                (acc, i) => {
                    acc.nitems += Number(i.nitems || i.in_stock || 0);
                    acc.reserved += Number(i.reserved || 0);
                    acc.available += Number(i.available || 0);
                    return acc;
                },
                { nitems: 0, reserved: 0, available: 0 }
            );
        },
        totalInStock() {
            return this.locationBreakdown.reduce((s, l) => s + Number(l.nitems || 0), 0);
        },
        totalReserved() {
            return this.locationBreakdown.reduce((s, l) => s + Number(l.reserved || 0), 0);
        },
        totalAvailable() {
            return this.locationBreakdown.reduce((s, l) => s + Number(l.available || 0), 0);
        },
        totalLayawayed() {
            return this.locationBreakdown.reduce((s, l) => s + Number(l.layawayed || 0), 0);
        },
        totalMissing() {
            return this.locationBreakdown.reduce((s, l) => s + Number(l.missing || 0), 0);
        },
    },
    methods: {
        // ============================================
        // INIT
        // ============================================
        initializeUser() {
            try {
                this.packet = JSON.parse(localStorage.packet || "{}");
                this.packet.userDetails = JSON.parse(localStorage.userDetails || "{}");
                this.isAdmin = this.packet.userDetails.userrole === "admin";
                this.user_location_id =
                    this.packet.userDetails.location_id ||
                    this.packet.userDetails.location ||
                    this.packet.userDetails.locationId ||
                    null;

                if (Array.isArray(this.packet.categories)) {
                    this.categoryOptions = [
                        { value: null, text: "ALL" },
                        ...this.packet.categories.filter(
                            (c) => c.value !== null && c.value !== undefined
                        ),
                    ];
                }
                if (Array.isArray(this.packet.vendors)) {
                    this.vendorOptions = [
                        { value: null, text: "ALL" },
                        ...this.packet.vendors.filter(
                            (v) => v.value !== null && v.value !== undefined
                        ),
                    ];
                }
                if (Array.isArray(this.packet.locations)) {
                    this.locationOptions = [
                        { value: null, text: "ALL" },
                        ...this.packet.locations.filter(
                            (l) => l.value !== null && l.value !== undefined
                        ),
                    ];
                }
                if (!this.isAdmin) {
                    this.location_id = this.user_location_id;
                }
            } catch (err) {
                console.error("Error parsing user data:", err);
            }
        },

        fetchSubcategories() {
            const path = "/api/product/get_subcategories/";
            const that = this;
            Requestor.postSyncP(
                path,
                {},
                this,
                (response) => {
                    if (response.data && !response.data.error) {
                        that.subcategories = response.data.result || [];
                    }
                },
                (error) => {
                    console.error("Error loading subcategories:", error);
                }
            );
        },

        // ============================================
        // SEARCH
        // ============================================
        runSearch() {
            this.loading = true;
            this.hasSearched = true;

            const path = "/api/inventory/search_product_inventory/";
            const data = {
                product_query: this.searchQuery || null,
                category_id: this.category_id,
                subcategory_id: this.subcategory_id,
                vendor_id: this.vendor_id,
                location_id: this.isAdmin ? this.location_id : this.user_location_id,
                new_arrival_only: this.onlyNewArrival,
                recently_received_only: this.onlyRecentlyReceived,
                user_role: this.packet.userDetails.userrole,
            };

            const that = this;
            Requestor.postSyncP(
                path,
                data,
                this,
                (response) => {
                    that.loading = false;
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {
                            title: "Error",
                            variant: "warning",
                            autoHideDelay: 2500,
                        });
                        that.items = [];
                    } else {
                        that.items = (response.data.result || []).map((item) => ({
                            ...item,
                            edit: false,
                            nitems: item.nitems || item.in_stock || 0,
                            reserved: item.reserved || 0,
                            available: item.available || 0,
                        }));
                    }
                    if (that.sort_key) that.applySort();
                },
                (error) => {
                    that.loading = false;
                    console.error("Search error:", error);
                    that.$bvToast.toast("Error searching products.", {
                        title: "Error",
                        variant: "danger",
                        autoHideDelay: 2500,
                    });
                }
            );
        },

        resetFilters() {
            this.searchQuery = "";
            this.category_id = null;
            this.subcategory_id = null;
            this.vendor_id = null;
            this.location_id = this.isAdmin ? null : this.user_location_id;
            this.onlyNewArrival = false;
            this.onlyRecentlyReceived = false;
            this.items = [];
            this.hasSearched = false;
        },

        removeFilter(key) {
            switch (key) {
                case "search": this.searchQuery = ""; break;
                case "category":
                    this.category_id = null;
                    this.subcategory_id = null;
                    break;
                case "subcategory": this.subcategory_id = null; break;
                case "vendor": this.vendor_id = null; break;
                case "location":
                    this.location_id = this.isAdmin ? null : this.user_location_id;
                    break;
                case "newArrival": this.onlyNewArrival = false; break;
                case "recentlyReceived": this.onlyRecentlyReceived = false; break;
            }
        },

        onCategoryChange() {
            this.subcategory_id = null;
        },

        // ============================================
        // SORTING
        // ============================================
        sortby(key) {
            if (this.sort_key === key) {
                this.sort_asc = !this.sort_asc;
            } else {
                this.sort_key = key;
                this.sort_asc = true;
            }
            this.applySort();
        },
        applySort() {
            if (!this.sort_key) return;
            const key = this.sort_key;
            const asc = this.sort_asc;
            this.items = [...this.items].sort((a, b) => {
                let av = a[key];
                let bv = b[key];
                if (typeof av === "string") av = av.toLowerCase();
                if (typeof bv === "string") bv = bv.toLowerCase();
                if (av == null) av = "";
                if (bv == null) bv = "";
                if (av < bv) return asc ? -1 : 1;
                if (av > bv) return asc ? 1 : -1;
                return 0;
            });
        },
        sortIcon(key) {
            if (this.sort_key !== key) return "arrow-down-up";
            return this.sort_asc ? "arrow-up" : "arrow-down";
        },

        // ============================================
        // HELPERS
        // ============================================
        formatNumber(n) {
            const num = Number(n) || 0;
            return num.toLocaleString();
        },
        itemKey(item, idx) {
            if (!item) return "row-" + idx;
            return (item.product_sku || item.sku || "row") + "-" + idx;
        },
        getItemSku(item) {
            if (!item) return "";
            return item.product_sku || item.sku || "";
        },
        getCategoryName(id) {
            if (!id) return "—";
            const dict = this.packet.categoriesdict || {};
            if (dict[id]) return dict[id];
            const opt = (this.packet.categories || []).find(
                (c) => String(c.value) === String(id)
            );
            return opt ? opt.text : "—";
        },
        getVendorName(id) {
            if (!id) return "—";
            const dict = this.packet.vendorsdict || {};
            if (dict[id]) return dict[id];
            const opt = (this.packet.vendors || []).find(
                (v) => String(v.value) === String(id)
            );
            return opt ? opt.text : "—";
        },
        getSubcategoryName(id) {
            if (!id) return "—";
            const s = this.subcategories.find((x) => String(x.id) === String(id));
            return s ? s.subcategory || s.name : "—";
        },
        getLocationName(loc) {
            if (!loc) return "Unknown";
            if (loc.location_name) return loc.location_name;
            if (loc.locsub_name) return loc.locsub_name;
            const id = loc.locsub || loc.location_id || loc.value;
            const dict = this.packet.locsubsdict || {};
            if (id && dict[id]) return dict[id];
            const opt = (this.packet.locations || []).find(
                (l) => String(l.value) === String(id)
            );
            return opt ? opt.text : `Location ${id || "?"}`;
        },
        getItemLocationCount(item) {
            if (!item) return 0;
            if (Array.isArray(item.location_breakdown)) return item.location_breakdown.length;
            return item.location_count || 0;
        },

        // ============================================
        // IMAGE HANDLING
        // ============================================
        getProductImage(item) {
            if (!item) return null;
            let url = item.image_url;
            if (!url) return null;
            url = String(url).trim();
            if (url === "" || url === "null" || url === "undefined") return null;
            if (url.includes("fmapi.myfurnituremecca.com")) {
                return `/api/image/image-proxy/?url=${encodeURIComponent(url)}`;
            }
            return url;
        },

        showFullImage(item) {
            if (!item) return;
            const url = this.getProductImage(item);
            if (!url) {
                this.$bvToast.toast("No image available for this product.", {
                    title: "Info",
                    variant: "info",
                    autoHideDelay: 2000,
                });
                return;
            }
            this.currentFullSizeImage = url;
            this.currentProductName =
                item.product_name || item.product_sku || item.sku || "Product Image";
            this.currentProductSku = item.product_sku || item.sku || "";
            this.$bvModal.show("modal-image-viewer");
        },

        onImageViewerHidden() {
            this.currentFullSizeImage = null;
            this.currentProductName = null;
            this.currentProductSku = null;
        },

        onFullImageError(event) {
            event.target.style.display = "none";
            this.currentFullSizeImage = null;
        },

        handleThumbError(event) {
            const img = event.target;
            const parent = img.parentElement;
            if (!parent) return;
            img.style.display = "none";
            if (!parent.querySelector(".no-image-thumb")) {
                const placeholder = document.createElement("div");
                placeholder.className = "no-image-thumb";
                placeholder.innerHTML =
                    '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#adb5bd" viewBox="0 0 16 16">' +
                    '<path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>' +
                    '<path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z"/>' +
                    "</svg>";
                parent.appendChild(placeholder);
            }
        },

        // ============================================
        // LOCATION BREAKDOWN MODAL
        // ============================================
        openLocationBreakdown(item) {
            this.selectedProduct = item || {};
            this.locationBreakdown = [];
            this.loadingBreakdown = true;
            this.$bvModal.show("modal-location-breakdown");

            const path = "/api/inventory/loc_sub_product_inventory/";
            const data = { product_query: this.selectedProductSku };
            if (this.isAdmin) data.location_id = this.location_id;
            else if (this.user_location_id) data.location_id = this.user_location_id;

            const that = this;
            Requestor.postSyncP(
                path,
                data,
                this,
                (response) => {
                    that.loadingBreakdown = false;
                    if (response.data.result) {
                        that.locationBreakdown = response.data.result.map((loc) => ({
                            ...loc,
                            editing: false,
                            edit_nitems: loc.nitems || 0,
                        }));
                    } else {
                        that.locationBreakdown = [];
                    }
                },
                (error) => {
                    that.loadingBreakdown = false;
                    console.error("Error loading location breakdown:", error);
                    that.$bvToast.toast("Error loading location breakdown.", {
                        title: "Error",
                        variant: "danger",
                        autoHideDelay: 2500,
                    });
                }
            );
        },

        onBreakdownModalHidden() {
            this.selectedProduct = {};
            this.locationBreakdown = [];
            this.loadingBreakdown = false;
            this.savingLocation = false;
        },

        startEditLocation(loc) {
            loc.editing = true;
            loc.edit_nitems = loc.nitems || 0;
        },
        cancelEditLocation(loc) {
            loc.editing = false;
            loc.edit_nitems = loc.nitems || 0;
        },

        saveLocationInventory(loc) {
            const newQty = parseInt(loc.edit_nitems, 10);
            if (isNaN(newQty) || newQty < 0) {
                this.$bvToast.toast("Please enter a valid quantity >= 0.", {
                    title: "Invalid Quantity",
                    variant: "warning",
                    autoHideDelay: 2000,
                });
                return;
            }
            this.savingLocation = true;
            const path = "/api/inventory/loc_sub_product_inventory_update/";
            const data = {
                ...loc,
                nitems: newQty,
                product_sku: this.selectedProductSku,
                product_name: this.selectedProductName,
                user_details: {
                    username: this.packet.userDetails.username,
                    user_id: this.packet.userDetails.user_id,
                },
            };
            const that = this;
            Requestor.postSyncP(
                path,
                data,
                this,
                (response) => {
                    that.savingLocation = false;
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {
                            title: "Error",
                            variant: "warning",
                            autoHideDelay: 2500,
                        });
                    } else {
                        loc.nitems = newQty;
                        loc.available = newQty - (loc.reserved || 0);
                        loc.editing = false;
                        that.$bvToast.toast("Inventory updated.", {
                            title: "Success",
                            variant: "success",
                            autoHideDelay: 1500,
                        });
                    }
                },
                (error) => {
                    that.savingLocation = false;
                    console.error("Update error:", error);
                    that.$bvToast.toast("Error updating inventory.", {
                        title: "Error",
                        variant: "danger",
                        autoHideDelay: 2500,
                    });
                }
            );
        },

        viewLocationLogs(loc) {
            const locName = this.getLocationName(loc);
            this.$bvToast.toast(
                `Move logs for ${locName} - hook this up to your Inventory Move Logs component.`,
                { title: "Info", variant: "info", autoHideDelay: 3000 }
            );
        },
    },
    created() {
        this.initializeUser();
        this.fetchSubcategories();
    },
};
</script>

<style scoped>
/* ============================================ */
/* BACKGROUND IMAGE + BLUR + GRADIENT OVERLAY   */
/* ============================================ */
.search-product-page.glass-theme {
    position: relative;
    max-width: 99%;
    width: 98%;
    margin: 0 auto;
    padding: 24px 8px 48px;
    font-size: 16px;
    color: #1f2937;
    min-height: 100vh;
    background: transparent;
}

.glass-theme::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: blur(30px) saturate(140%) brightness(0.9);
    opacity: 0.5;
    z-index: -2;
    transform: scale(1.15);
    pointer-events: none;
}

.glass-theme::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
        radial-gradient(circle at 15% 10%, rgba(255,255,255,0.25), transparent 50%),
        radial-gradient(circle at 85% 90%, rgba(30,40,60,0.3), transparent 55%),
        linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(240,244,250,0.75) 100%);
    z-index: -1;
    pointer-events: none;
}

@keyframes fadeInPage {
    from { opacity: 0; transform: translateY(24px); filter: blur(2px); }
    to   { opacity: 1; transform: translateY(0); filter: blur(0); }
}
.fade-in-page { animation: fadeInPage 0.7s cubic-bezier(0.2, 0.9, 0.4, 1.1); }

/* ============================================ */
/* GLASS SURFACES                               */
/* ============================================ */
.glass-card {
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(18px) saturate(160%);
    -webkit-backdrop-filter: blur(18px) saturate(160%);
    border: 1px solid rgba(255, 255, 255, 0.5) !important;
    border-radius: 16px !important;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08) !important;
}

/* ============================================ */
/* HEADER                                       */
/* ============================================ */
.page-header {
    margin-bottom: 24px;
    padding: 4px 2px;
}

.page-title {
    font-size: 2rem;
    font-weight: 800;
    color: #111827;
    margin: 0 0 6px;
    letter-spacing: -0.4px;
    text-shadow: 0 1px 2px rgba(255,255,255,0.6);
}

.page-subtitle {
    font-size: 1.05rem;
    color: #4b5563;
    margin: 0;
    font-weight: 500;
}

/* ============================================ */
/* STATS CARDS — GRADIENT, LARGE                */
/* ============================================ */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 18px;
    margin-bottom: 24px;
}

.stat-card {
    border-radius: 16px;
    padding: 24px 26px;
    color: #fff;
    position: relative;
    overflow: hidden;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transition: transform 0.22s ease, box-shadow 0.22s ease;
    min-height: 120px;
    display: flex;
    align-items: center;
}

.stat-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.18);
}

.stat-card::after {
    content: '';
    position: absolute;
    right: -40px;
    top: -40px;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
}

.stat-card-primary {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}
.stat-card-info {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}
.stat-card-warning {
    background: linear-gradient(135deg, #f7971e 0%, #ffd200 100%);
}
.stat-card-success {
    background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
}

.stat-card-inner {
    display: flex;
    align-items: center;
    gap: 18px;
    position: relative;
    z-index: 1;
    width: 100%;
}

.stat-icon {
    background: rgba(255, 255, 255, 0.25);
    border-radius: 14px;
    width: 62px;
    height: 62px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    backdrop-filter: blur(8px);
}

.stat-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}

.stat-label {
    font-size: 0.9rem;
    font-weight: 700;
    opacity: 0.95;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
}

.stat-value {
    font-size: 2.4rem;
    font-weight: 900;
    line-height: 1;
    letter-spacing: -1px;
    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* ============================================ */
/* FILTERS                                      */
/* ============================================ */
.filters-wrapper {
    padding: 22px 24px;
    margin-bottom: 20px;
}

.filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1.1rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 16px;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba(0,0,0,0.06);
}

.filter-count {
    font-size: 0.9rem;
    font-weight: 500;
    color: #6b7280;
}

.filter-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 700;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-bottom: 8px;
}

.filters-wrapper .form-control,
.filters-wrapper .custom-select {
    border-radius: 10px;
    border: 1.5px solid rgba(0,0,0,0.1);
    font-size: 1rem;
    color: #1f2937;
    background: rgba(255, 255, 255, 0.85);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.filters-wrapper .form-control:focus,
.filters-wrapper .custom-select:focus {
    border-color: #4f46e5;
    box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.12);
    outline: none;
}

.filters-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(0,0,0,0.06);
    flex-wrap: wrap;
    gap: 12px;
}

.checkboxes-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 24px;
    font-size: 1rem;
}

.checkboxes-group .custom-control-label {
    font-size: 1rem;
    color: #374151;
    font-weight: 500;
}

.buttons-group {
    display: flex;
    gap: 10px;
}

/* ---------- Active filters ---------- */
.active-filters {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(0,0,0,0.06);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    font-size: 0.95rem;
}

.active-filters-label {
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.85rem;
    letter-spacing: 0.4px;
}

.active-filter-tag {
    display: inline-flex;
    align-items: center;
    background: #eef2f7;
    color: #374151;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.92rem;
    font-weight: 500;
    border: 1px solid rgba(0,0,0,0.06);
}

/* ============================================ */
/* RESULTS                                      */
/* ============================================ */
.results-container {
    padding: 20px;
}

.results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
    padding: 0 4px;
    font-size: 1rem;
    color: #4b5563;
}

.results-count strong {
    color: #111827;
    font-weight: 800;
    font-size: 1.05rem;
}

/* ---------- Results table ---------- */
.results-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 1rem;
    table-layout: auto;
}

.results-table thead th {
    background: rgba(243, 244, 246, 0.9);
    border-bottom: 2px solid rgba(0,0,0,0.08);
    padding: 16px 12px;
    text-align: left;
    font-weight: 700;
    color: #4b5563;
    font-size: 0.82rem;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    white-space: nowrap;
    user-select: none;
}

.results-table thead th.sortable {
    cursor: pointer;
    transition: color 0.15s;
}

.results-table thead th.sortable:hover {
    color: #4f46e5;
}

.results-table tbody td {
    border-bottom: 1px solid rgba(0,0,0,0.06);
    padding: 16px 12px;
    vertical-align: middle;
    color: #374151;
    background: transparent;
    font-size: 1rem;
}

.results-table tbody tr:last-child td {
    border-bottom: none;
}

.results-table tbody tr:hover td {
    background: rgba(249, 250, 251, 0.9);
}

.results-table .text-left {
    text-align: left;
}

.results-table td:not(.text-left) {
    text-align: center;
}

/* ---------- Larger, cleaner cells for wide columns ---------- */
.sku-cell {
    display: block;
    font-size: 1.05rem;
    font-weight: 800;
    color: #111827;
    letter-spacing: 0.3px;
    margin-bottom: 4px;
}

.name-cell {
    font-size: 1.05rem !important;
    font-weight: 600;
    color: #111827 !important;
    line-height: 1.35;
}

.vendor-cell {
    font-size: 1.02rem !important;
    font-weight: 500;
    color: #1f2937 !important;
    line-height: 1.35;
}

.category-cell {
    font-size: 1.02rem !important;
    font-weight: 500;
    color: #1f2937 !important;
    line-height: 1.35;
}

.subcategory-cell {
    font-size: 1.02rem !important;
    font-weight: 500;
    color: #1f2937 !important;
    line-height: 1.35;
}

.tags-row {
    margin-top: 6px;
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
}

.mini-tag {
    display: inline-block;
    font-size: 0.78rem;
    font-weight: 700;
    color: #4b5563;
    background: rgba(243, 244, 246, 0.9);
    padding: 3px 8px;
    border-radius: 6px;
    letter-spacing: 0.3px;
    border: 1px solid rgba(0,0,0,0.06);
}

.qty-pill {
    display: inline-block;
    padding: 8px 16px;
    border-radius: 20px;
    font-weight: 800;
    font-size: 1.05rem;
    min-width: 60px;
    letter-spacing: 0.3px;
}

.qty-primary {
    background: #e0e7ff;
    color: #3730a3;
}
.qty-warning {
    background: #fef3c7;
    color: #92400e;
}
.qty-success {
    background: #d1fae5;
    color: #065f46;
}

.locations-badge {
    display: inline-flex;
    align-items: center;
    background: #eef2f7;
    color: #4b5563;
    padding: 6px 12px;
    border-radius: 20px;
    font-weight: 700;
    font-size: 1rem;
    border: 1px solid rgba(0,0,0,0.06);
}

.results-table tfoot .totals-row td {
    background: rgba(243, 244, 246, 0.9);
    border-top: 2px solid rgba(0,0,0,0.08);
    padding: 16px 12px;
    text-align: center;
    font-weight: 800;
    color: #111827;
    font-size: 1rem;
}

/* ============================================ */
/* IMAGE CELL — MUCH BIGGER                     */
/* ============================================ */
.image-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 96px;
    height: 96px;
    margin: 0 auto;
    border-radius: 12px;
    background: rgba(250, 250, 250, 0.9);
    border: 2px solid rgba(0,0,0,0.08);
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
}

.image-cell:hover {
    border-color: #4f46e5;
    transform: scale(1.06);
    box-shadow: 0 8px 20px rgba(79, 70, 229, 0.25);
}

.product-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.25s ease;
}

.image-cell:hover .product-thumb {
    transform: scale(1.08);
}

.no-image-thumb {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(249, 250, 251, 0.9);
    color: #d1d5db;
}

/* ============================================ */
/* FULL-SIZE IMAGE VIEWER                       */
/* ============================================ */
.full-size-image {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    border-radius: 10px;
    background: #fafafa;
    box-shadow: 0 8px 30px rgba(0,0,0,0.15);
}

.image-fallback-large {
    padding: 60px 20px;
    background: #fafafa;
    border: 2px dashed #e5e7eb;
    border-radius: 10px;
}

/* ============================================ */
/* EMPTY STATES                                 */
/* ============================================ */
.empty-state {
    text-align: center;
    padding: 70px 20px;
    margin-top: 16px;
}

.empty-state h5 {
    color: #374151;
    font-weight: 800;
    margin-top: 14px;
    font-size: 1.2rem;
}

.empty-state p {
    font-size: 1rem;
    color: #6b7280;
    margin: 8px auto 0;
    max-width: 500px;
}

/* ============================================ */
/* MODAL — product summary + table              */
/* ============================================ */
.product-summary-card {
    background: linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%);
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px 22px;
}

.summary-label {
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #6b7280;
    margin-bottom: 6px;
}

.summary-value {
    font-size: 1.2rem;
    font-weight: 800;
    color: #111827;
}

.location-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 1rem;
}

.location-table thead th {
    background: linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%);
    border-bottom: 2px solid #bae6fd;
    padding: 14px 10px;
    text-align: left;
    font-weight: 700;
    color: #0c4a6e;
    font-size: 0.82rem;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    white-space: nowrap;
}

.location-table tbody td {
    border-bottom: 1px solid #f0f3f7;
    padding: 14px 10px;
    vertical-align: middle;
    color: #374151;
    text-align: center;
    font-size: 1rem;
}

.location-table tbody tr:hover td {
    background: #f8fafc;
}

.location-table .loc-name {
    text-align: left;
    font-weight: 700;
    color: #111827;
}

.location-table tfoot .totals-row td {
    background: #e0f2fe;
    border-top: 2px solid #7dd3fc;
    padding: 14px 10px;
    text-align: center;
    font-weight: 800;
    color: #0c4a6e;
    font-size: 1rem;
}

.edit-input {
    max-width: 110px;
    margin: 0 auto;
    text-align: center;
    border-radius: 8px;
    border: 1.5px solid #4f46e5;
    font-size: 1rem;
    font-weight: 700;
    background: #fffbea;
}

.edit-input:focus {
    border-color: #4f46e5;
    box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.12);
}

/* ============================================ */
/* RESPONSIVE                                   */
/* ============================================ */
@media (max-width: 1200px) {
    .results-table { font-size: 0.95rem; }
    .results-table thead th,
    .results-table tbody td { padding: 14px 10px; }
    .image-cell { width: 82px; height: 82px; }
}

@media (max-width: 992px) {
    .page-title { font-size: 1.7rem; }
    .stat-value { font-size: 2rem; }
    .stat-icon { width: 54px; height: 54px; }
    .image-cell { width: 72px; height: 72px; }
    .qty-pill { padding: 6px 12px; font-size: 1rem; min-width: 52px; }
}

@media (max-width: 768px) {
    .search-product-page.glass-theme { padding: 16px 6px 32px; }
    .page-title { font-size: 1.4rem; }
    .page-subtitle { font-size: 0.95rem; }

    .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
    .stat-card { padding: 16px 18px; min-height: 100px; }
    .stat-value { font-size: 1.6rem; }
    .stat-label { font-size: 0.75rem; }
    .stat-icon { width: 46px; height: 46px; border-radius: 10px; }

    .filters-wrapper { padding: 16px 18px; }
    .filters-bottom { flex-direction: column; align-items: stretch; }
    .buttons-group { justify-content: stretch; }
    .buttons-group .btn { flex: 1; }

    .results-table { font-size: 0.9rem; }
    .results-table thead th,
    .results-table tbody td { padding: 12px 6px; }

    .image-cell { width: 60px; height: 60px; }

    .location-table { font-size: 0.9rem; }
    .location-table thead th,
    .location-table tbody td { padding: 10px 6px; }
}

@media (max-width: 480px) {
    .stats-grid { grid-template-columns: 1fr; }
    .page-title { font-size: 1.25rem; }
    .image-cell { width: 52px; height: 52px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Inventory/SearchProduct.vue